import { Box, Modal, Text } from '@fozito/common-ui';
import { Col, Row } from 'antd';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { IResponseData } from 'services/response';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import delayTime from 'utils/await';
import { formatCurrency } from 'utils/formatCurrency';
import { setPageLoading } from 'widgets/PageLoading';
import { useTicketWrapper } from '../Receipts/context';
import cashierService from '../services';
import cashierApis from '../services/apis';
import { IBillInfoResData } from '../services/types/bill';
export type ConfirmTipTicketRef = {
  open: (id: string, cb: () => void, ignorePrint?: boolean) => void;
  close: () => void;
};

const useConfirmTipTicketRef = () => useRef<ConfirmTipTicketRef>(null);
const ConfirmTipTicket = forwardRef<ConfirmTipTicketRef>(({ }, ref) => {
  const [open, setOpen] = useState(false);
  const cbRef = useRef<() => void>(() => undefined);
  const [detail, setDetail] = useState<IBillInfoResData | null>(null);
  const dispatch = useAppDispatch();
  const ticketContext = useTicketWrapper();
  const ignorePrint = useRef(false);

  const calc = useMemo(() => ({
    serviceCharge: formatCurrency(detail?.cardFee),
    discount: formatCurrency(-(detail?.discount || 0)),
    subTotal: formatCurrency((detail?.subTotal || 0) - (detail?.discount || 0)),
    balance: formatCurrency(detail?.subTotal),
    tax: formatCurrency(detail?.tax),
    total: formatCurrency(detail?.total),
  }), [detail]);

  const getDetailTicket = async (billId: string) => {
    setPageLoading(true);
    try {
      const res: IResponseData<IBillInfoResData> = await cashierApis.getBillById(billId);
      if (res.data.data) return res.data.data;
      else return null;
    } catch (error) {
      return null;
    } finally {
      setPageLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    open: async (id: string, cb, _ignorePrint) => {
      if (!id) return;
      dispatch(cashierService.actions.resetPrinter());
      const data = await getDetailTicket(id);
      if (data) {
        dispatch(cashierService.actions.saveTicketPrinterDetail(data));
        cbRef.current = cb;
        ignorePrint.current = !!_ignorePrint;
        setDetail(data);
        setOpen(true);
      }
    },
    close: () => setOpen(false),
  }));

  const handleComplete = async () => {
    if (!detail?.billId) return;
    setOpen(false);
    if (ignorePrint.current) {
      cbRef.current();
      setPageLoading(false);
      return;
    }
    setPageLoading(true);
    try {
      const res: IResponseData<any> = await cashierApis.getCouponPrint(detail?.billId);
      if (res.data.data) {
        dispatch(cashierService.actions.saveTicketPrinterCoupons(res.data.data.coupons));
      }
    } catch (error) {
    }
    dispatch(cashierService.actions.resetCashierMessage());
    await delayTime(50);
    cbRef.current();
    // const isCreditCard = detail.payments?.find(o => o.paymentType === PAYMENT_TYPE.CREDIT_CARD);
    // if (isCreditCard && !detail.signature) {
    //   ticketContext.doPrintDoubleReceipt();
    //   setPageLoading(false);
    //   return;
    // }
    // ticketContext.doPrint();
    ticketContext.doPrintDoubleReceipt();
    setPageLoading(false);
  };

  if (!open) return null;

  return (
    <ModalStyled
      open
      title=''
      width={900}
      className='modal-overflow-unset modal-max-height-unset'
      onClose={handleComplete}
      styles={{ header: { display: 'none' }, body: { maxHeight: 'unset', overflow: 'unset' }, content: { borderRadius: 8 } }}
    >
      <TicketDetailStyled>
        <Row justify={'space-between'} style={{ marginBottom: '1rem' }}>
          <Box display='flex' alignItems='center' gap={8}>
            <Text className='box-number'><Text title_h1_36_semibold color_text_1>#{detail?.ticketNumber}</Text></Text>
            <Text title_h9_18_semibold>{detail?.createdDated}</Text>
          </Box>
          <Text title_h5_24_semibold_caplock>{detail?.customerName || 'Unknown Customer'}</Text>
        </Row>
        <div className="items">
          <div className='item'>
            {detail?.items.map((s, i) => (
              <Row key={i} align={'middle'} justify={'space-between'} className='service'>
                <Text title_h8_18_medium>{s.itemName}</Text>
                <Box display='flex' alignItems='center' gap={8}>

                  <Text title_h7_20_semibold>x{s.quantity}</Text>
                  <ThroughLineText style={{ width: 120, opacity: 0 }}>{formatCurrency(s.price || 0)}</ThroughLineText>
                  <Text title_h9_18_semibold text_right style={{ width: 120 }}>{formatCurrency(s.price || 0)}</Text>
                </Box>
              </Row>
            ))}
          </div>
        </div>
        <Text className="border" />
        <Row className="group-info">
          <Col flex={'auto'}>
            <div className="item">
              <Text title_h8_18_medium>Processing Fee:</Text>
              <Text title_h8_18_medium text_right>{calc.serviceCharge}</Text>
            </div>
            <div className="item">
              <Text title_h8_18_medium>Discount:</Text>
              <Text title_h8_18_medium text_right>{calc.discount || 0}</Text>
            </div>
            <div className="item">
              <Text title_h8_18_medium>Subtotal:</Text>
              <Text title_h8_18_medium text_right>{calc.subTotal}</Text>
            </div>

          </Col>
          <Col flex={'auto'} />
          <Col flex={'auto'}>
            <div className="item">
              <Text title_h8_18_medium>Balance:</Text>
              <Text title_h8_18_medium text_right>{calc.balance}</Text>
            </div>
            <div className="item">
              <Text title_h8_18_medium>Tax:</Text>
              <Text title_h8_18_medium text_right>{calc.tax}</Text>
            </div>
          </Col>
        </Row>
        <Text className="border" />
        <Box display='flex' justifyContent='flex-end' gap={24}>
          <Text title_h5_24_semibold>Total:</Text>
          <Text title_h5_24_semibold style={{ color: '#D2464F' }}>{calc.total}</Text>
        </Box>
        <Box margin={16} />
      </TicketDetailStyled>
      <Box display='flex' justifyContent='center' marginBottom={16}>
        <button className='buttonCompleted' onClick={handleComplete}>
          <div className='icon-tick'><IconTick /></div>
          <Text content_content_3_16_semibold style={{ color: '#fff', padding: '0 1rem' }}>Completed</Text>
        </button>
      </Box>
    </ModalStyled>
  );
});
ConfirmTipTicket.displayName = 'ConfirmTipTicket';
const useConfirmTipTicket = (): [React.RefObject<ConfirmTipTicketRef>, JSX.Element] => {
  const ref = useConfirmTipTicketRef();
  const context = <ConfirmTipTicket ref={ref} />;
  return [ref, context];
};
export default useConfirmTipTicket;

const ThroughLineText = styled(Text)`
color: #D2464F;
text-align: right;
font-family: Poppins;
font-size: 18px;
font-style: normal;
font-weight: 600;
line-height: 30px; /* 166.667% */
text-decoration: line-through;
text-transform: capitalize;
`;

const ModalStyled = styled(Modal)`
zoom: 1.2;

.buttonCompleted {
  display: flex;
  align-items: center;
  border-radius: 6px;
  height: 56px;
  background: #292C33;
  border-radius: 8px;
  overflow: hidden;
  .icon-tick {
    width: 56px;
    background: #759F50;
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
`;
const TicketDetailStyled = styled.div`
padding: 16px;
display: flex;
flex-direction: column;
.btn-back {
  position: fixed;
  top: 0.5rem;
  right: 2.5rem;
}
.box-number {
  background: #000;
  padding: 4px 8px;
  border-radius: 4px;
  span {
    color: #fff;
  }
}
  .items {
    max-height: 50vh;
    overflow: auto;
    .border-dotted {
      border: 1px dotted #86909C;
    }
    .item {
      margin-bottom: 1rem;
    }
    .service {
      margin-bottom: 0.5rem;
    }
  }
  .border {
    border: 0.75px solid #86909C;
  }
  .box-info {
    margin-bottom: 2rem;
  }
  .group-info {
    .item {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0.5rem;
      .H8, .H7 {
        font-size: 1.5rem;
      }
    }
  }
`;

const IconTick = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M9.72692 18C9.40676 18 9.10261 17.8661 8.8785 17.6317L4.34817 12.8936C3.88394 12.4081 3.88394 11.6045 4.34817 11.1189C4.8124 10.6334 5.58078 10.6334 6.04501 11.1189L9.72692 14.9697L17.955 6.36414C18.4192 5.87862 19.1876 5.87862 19.6518 6.36414C20.1161 6.84967 20.1161 7.6533 19.6518 8.13882L10.5753 17.6317C10.3512 17.8661 10.0471 18 9.72692 18Z" fill="#fff" />
  </svg>
);
